import React, { useState, useEffect, useRef } from 'react';
import { List, Avatar, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useWebSocket, OnlineUser } from '../../contexts/WebSocketProvider';
import './MentionsAutocomplete.css';

const { Text } = Typography;

interface MentionsAutocompleteProps {
  show: boolean;
  position: { top: number; left: number };
  query: string;
  onSelect: (user: OnlineUser) => void;
  onClose: () => void;
  currentRoomId: string | null;
}

const MentionsAutocomplete: React.FC<MentionsAutocompleteProps> = ({
  show,
  position,
  query,
  onSelect,
  onClose,
  currentRoomId
}) => {
  const [filteredUsers, setFilteredUsers] = useState<OnlineUser[]>([]);
  const { onlineMembers } = useWebSocket();
  const listRef = useRef<HTMLDivElement>(null);

  // Фильтруем пользователей Matrix (поддержка) при изменении query
  useEffect(() => {
    if (!show || !currentRoomId || !onlineMembers?.rooms[currentRoomId]) {
      setFilteredUsers([]);
      return;
    }

    const roomData = onlineMembers.rooms[currentRoomId];
    const matrixUsers = [
      ...roomData.matrix_users.online,
      ...roomData.matrix_users.offline,
      ...roomData.matrix_users.unavailable,
      ...roomData.matrix_users.unknown
    ];

    // Фильтруем по query
    const filtered = matrixUsers.filter(user => {
      if (!user.user_id) return false;
      const searchText = query.toLowerCase();
      return (
        user.user_id.toLowerCase().includes(searchText) ||
        (user.display_name && user.display_name.toLowerCase().includes(searchText)) ||
        user.user_id.split(':')[0].substring(1).toLowerCase().includes(searchText) // localpart без @
      );
    }).slice(0, 10); // Ограничиваем количество результатов

    setFilteredUsers(filtered);
  }, [show, query, currentRoomId, onlineMembers]);

  // Закрываем при клике вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onClose]);

  if (!show || filteredUsers.length === 0) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return '#52c41a';
      case 'unavailable':
        return '#faad14';
      case 'offline':
      case 'unknown':
      default:
        return '#d9d9d9';
    }
  };

  return (
    <div 
      ref={listRef}
      className="mentions-autocomplete"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 1000,
        maxHeight: '200px',
        overflowY: 'auto',
        backgroundColor: 'white',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        minWidth: '250px'
      }}
    >
      <List
        size="small"
        dataSource={filteredUsers}
        renderItem={(user) => (
          <List.Item 
            className="mentions-autocomplete-item"
            onClick={() => onSelect(user)}
            style={{
              cursor: 'pointer',
              padding: '8px 12px'
            }}
          >
            <List.Item.Meta
              avatar={
                <div style={{ position: 'relative' }}>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <div
                    style={{
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(user.status),
                      border: '1px solid white'
                    }}
                  />
                </div>
              }
              title={
                <Text strong style={{ fontSize: '13px' }}>
                  {user.display_name || (user.user_id ? user.user_id.split(':')[0] : 'Unknown')}
                </Text>
              }
              description={
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {user.user_id}
                </Text>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
};

export default MentionsAutocomplete; 